import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  getGroupList,
  createGroup,
  updateGroup,
  CreateGroupPayload,
  UpdateGroupPayload,
} from "../../apis/userGroupApis";

interface GroupState {
  groups: any[];
  loading: boolean;
  error: string | null;
}

const initialState: GroupState = {
  groups: [],
  loading: false,
  error: null,
};

/* ================================
   FETCH GROUPS
================================ */

export const fetchGroups = createAsyncThunk(
  "groups/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getGroupList();
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.data || "Failed to fetch groups",
      );
    }
  },
);

/* ================================
   REFETCH GROUPS
================================ */

export const refetchGroups = createAsyncThunk(
  "groups/refetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getGroupList();
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.data || "Failed to refetch groups",
      );
    }
  },
);

/* ================================
   CREATE GROUP
================================ */

export const createGroupThunk = createAsyncThunk(
  "groups/create",
  async (payload: CreateGroupPayload, { rejectWithValue }) => {
    try {
      await createGroup(payload);
      const res = await getGroupList(); // refresh list
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.data || "Failed to create group",
      );
    }
  },
);

/* ================================
   UPDATE GROUP
================================ */

export const updateGroupThunk = createAsyncThunk(
  "groups/update",
  async (
    { groupId, payload }: { groupId: string; payload: UpdateGroupPayload },
    { rejectWithValue },
  ) => {
    try {
      await updateGroup(groupId, payload);
      const res = await getGroupList(); // refresh list
      return {
        groups: res.data,
        payload,
      };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.data || "Failed to update group",
      );
    }
  },
);

/* ================================
   SLICE
================================ */

const groupSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      /* ---------- fetch ---------- */
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to fetch groups");
      })

      /* ---------- refetch ---------- */
      .addCase(refetchGroups.pending, (state) => {
        state.loading = true;
      })
      .addCase(refetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(refetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to refetch groups");
      })

      /* ---------- create ---------- */
      .addCase(createGroupThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGroupThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
        toast.success("Group created successfully");
      })
      .addCase(createGroupThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to create group");
      })

      /* ---------- update ---------- */
      .addCase(updateGroupThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGroupThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.groups;

        const isDelete = action.payload.payload?.isActive === false;

        toast.success(
          isDelete
            ? "Group deleted successfully"
            : "Group updated successfully",
        );
      })
      .addCase(updateGroupThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to update group");
      });
  },
});

export default groupSlice.reducer;
